import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { CartItem } from '../types';

type CartContextValue = {
    items: CartItem[];
    totalQuantity: number;
    addItem: (productId: number, quantity?: number) => void;
    updateItemQuantity: (productId: number, quantity: number) => void;
    removeItem: (productId: number) => void;
    clearCart: () => void;
};

const CartContext = createContext<CartContextValue | undefined>(undefined);
const STORAGE_KEY = 'miniapp_cart_items_v1';

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw) as CartItem[];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            // ignore
        }
    }, [items]);

    const totalQuantity = useMemo(() => {
        return items.reduce((sum, i) => sum + i.quantity, 0);
    }, [items]);

    const value = useMemo<CartContextValue>(() => {
        return {
            items,
            totalQuantity,
            addItem: (productId, quantity = 1) => {
                setItems(prev => {
                    const existing = prev.find(i => i.productId === productId);
                    if (existing) {
                        return prev.map(i =>
                            i.productId === productId
                                ? { ...i, quantity: i.quantity + quantity }
                                : i
                        );
                    }
                    return [...prev, { productId, quantity }];
                });
            },
            updateItemQuantity: (productId, quantity) => {
                if (quantity <= 0) {
                    setItems(prev => prev.filter(i => i.productId !== productId));
                } else {
                    setItems(prev =>
                        prev.map(i =>
                            i.productId === productId ? { ...i, quantity } : i
                        )
                    );
                }
            },
            removeItem: productId => setItems(prev => prev.filter(i => i.productId !== productId)),
            clearCart: () => setItems([]),
        };
    }, [items, totalQuantity]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export function useCartStore(): CartContextValue {
    const ctx = useContext(CartContext);
    if (!ctx) {
        throw new Error('useCartStore deve essere usato dentro <CartProvider>');
    }
    return ctx;
}

export const useCart = useCartStore;
